import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useEditAssortmentMutation, useGetCartonListByProductMutation, useGetEditedCartonListQuery, useGetAllCartonListQuery } from "../../../../features/tenant/Lancer/LancerApi";
import TenantSlugs from "../../../../utils/TenantSlug";
import { exportToCSV } from "../../../../utils/exportCsv";

const EditAssortment = () => {
    // Slugs: 39 for staging (default/dev), 32 for prod.
    const currentSlug = TenantSlugs.lancer;

    const [getCartonListByProduct, { isLoading: isCartonListLoading }] = useGetCartonListByProductMutation();
    const [editAssortment, { isLoading: isAssortmentLoading }] = useEditAssortmentMutation();

    const [productCode, setProductCode] = useState("");
    const [cartonNo, setCartonNo] = useState("");
    const [searchType, setSearchType] = useState("product"); // "product" or "carton"
    const [cartonList, setCartonList] = useState([]);
    const [selectedCartons, setSelectedCartons] = useState([]);
    const [assortmentData, setAssortmentData] = useState(null); // structure: { [size]: qty }
    const [fetchedAssortment, setFetchedAssortment] = useState(null); // Store fetched assortment from search
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSearch = async () => {
        if (searchType === "product" && !productCode.trim()) {
            toast.error("Please enter a Product Code");
            return;
        }
        
        if (searchType === "carton" && !cartonNo.trim()) {
            toast.error("Please enter a Carton Number");
            return;
        }

        try {
            const params = {
                token: sessionStorage.getItem("access_token"),
                slug: currentSlug,
            };

            if (searchType === "product") {
                params.product_code = productCode;
            } else {
                params.carton_no = cartonNo;
            }

            const res = await getCartonListByProduct(params).unwrap();
            console.log(res);
            if (res.success) {
                setCartonList(res.body.cartonList || []);

                // Process fetched assortment for later use
                const formatted = {};
                if (res.body.assortment && Array.isArray(res.body.assortment)) {
                    res.body.assortment.forEach(item => {
                        formatted[item.size] = item.qty;
                    });
                }
                setFetchedAssortment(formatted);

                // Reset selection and editor
                setSelectedCartons([]);
                setIsEditing(false);
                setAssortmentData(null);

                if (res.body.cartonList?.length === 0) {
                    toast(res.message || "No cartons found for this criteria.", { icon: "ℹ️" });
                }
            } else {
                toast.error(res.message || "Failed to fetch carton list.");
            }
        } catch (error) {
            console.error("Search error", error);
            toast.error("Error searching for product/carton.");
        }
    };

    const handleCheckboxChange = (cartonNo) => {
        if (selectedCartons.includes(cartonNo)) {
            setSelectedCartons(selectedCartons.filter((c) => c !== cartonNo));
        } else {
            setSelectedCartons([...selectedCartons, cartonNo]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCartons(cartonList.map((c) => c.carton_no));
        } else {
            setSelectedCartons([]);
        }
    };

    const handleStartEdit = () => {
        if (selectedCartons.length === 0) {
            toast.error("Please select at least one carton.");
            return;
        }

        // Use the assortment fetched during search
        if (fetchedAssortment) {
            setAssortmentData({ ...fetchedAssortment }); // Copy to allow editing
            setIsEditing(true);
        } else {
            toast.error("No assortment data available for this product.");
        }
    };

    const handleAssortmentChange = (size, newQty) => {
        setAssortmentData(prev => ({
            ...prev,
            [size]: parseInt(newQty) || 0
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = {
                carton_no: selectedCartons,
                assortment: assortmentData
            };

            const res = await editAssortment({
                token: sessionStorage.getItem("access_token"),
                slug: currentSlug,
                body: payload
            }).unwrap();

            if (res.success) {
                toast.success("Assortment updated successfully!");
                setIsEditing(false);
                setSelectedCartons([]);
                setAssortmentData(null);
                refetchEditedCartons(); // Refresh the list of edited cartons
            } else {
                toast.error(res.message || "Update failed.");
            }

        } catch (error) {
            console.error("Update assortment error", error);
            toast.error("Error updating assortment.");
        } finally {
            setLoading(false);
        }
    };


    // Fetch edited cartons
    const { data: editedCartonsData, refetch: refetchEditedCartons } = useGetEditedCartonListQuery({
        token: sessionStorage.getItem("access_token"),
        slug: currentSlug,
    });

    const handleExportEditedCSV = () => {
        const listToExport = editedCartonsData?.body?.editedCartons;

        if (!listToExport || listToExport.length === 0) {
            toast.error("No edited cartons to export");
            return;
        }

        const exportColumns = [
            { name: "Carton Number", selector: row => row.carton_no },
            { name: "Article Name", selector: row => row.article_name || "-" },
            { name: "Product Code", selector: row => row.product_code },
            { name: "Product Name", selector: row => row.name || "-" },
            { name: "Color", selector: row => row.color || "-" },
            { name: "Size", selector: row => row.size || "-" }
        ];

        exportToCSV(listToExport, exportColumns, "edited_assortment_list.csv");
    };

    // columns definition wrapped in useMemo to avoid re-creation on every render
    const columns = React.useMemo(() => [
        {
            name: (
                <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={cartonList.length > 0 && selectedCartons.length === cartonList.length}
                />
            ),
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={selectedCartons.includes(row.carton_no)}
                    onChange={() => handleCheckboxChange(row.carton_no)}
                />
            ),
            width: "50px",
        },
        {
            name: "Carton Number",
            selector: (row) => row.carton_no,
            sortable: true,
        },
        {
            name: "Order No",
            selector: (row) => row.order_no || "-",
            sortable: true,
        },
    ], [cartonList, selectedCartons]);

    const editedColumns = [
        {
            name: "Carton Number",
            selector: (row) => row.carton_no,
            sortable: true,
        },
        {
            name: "Product Code",
            selector: (row) => row.product_code,
            sortable: true,
        },
        {
            name: "Article Name",
            selector: (row) => row.name || "-",
            sortable: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Color",
            selector: (row) => row.color,
            sortable: true,
        },
        {
            name: "Size",
            selector: (row) => row.size,
            sortable: true,
        },
    ];

    return (
        <div className="flex w-full bg-gray-200 min-h-screen justify-center">
            <div className="w-full">
                <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                    Edit Assortment
                </div>

                <div className="p-3">
                    {/* Search Section */}
                    <div className="bg-white shadow rounded-lg p-4 mb-4">
                        <h2 className="text-lg font-semibold mb-3">Search</h2>
                        <div className="flex gap-4 items-end flex-wrap">
                            <div className="flex flex-col">
                                <label className="text-sm font-medium text-gray-700 mb-1">Search By</label>
                                <select
                                    className="border border-gray-300 rounded px-3 py-2 focus:ring-primary focus:border-primary bg-white"
                                    value={searchType}
                                    onChange={(e) => setSearchType(e.target.value)}
                                >
                                    <option value="product">Product Code</option>
                                    <option value="carton">Carton Number</option>
                                </select>
                            </div>

                            {searchType === "product" ? (
                                <div className="flex flex-col flex-1 max-w-sm">
                                    <label className="text-sm font-medium text-gray-700 mb-1">Product Code</label>
                                    <input
                                        type="text"
                                        className="border border-gray-300 rounded px-3 py-2 focus:ring-primary focus:border-primary"
                                        placeholder="Enter Product Code"
                                        value={productCode}
                                        onChange={(e) => setProductCode(e.target.value)}
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-col flex-1 max-w-sm">
                                    <label className="text-sm font-medium text-gray-700 mb-1">Carton Number</label>
                                    <input
                                        type="text"
                                        className="border border-gray-300 rounded px-3 py-2 focus:ring-primary focus:border-primary"
                                        placeholder="Enter Carton Number"
                                        value={cartonNo}
                                        onChange={(e) => setCartonNo(e.target.value)}
                                    />
                                </div>
                            )}

                            <button
                                className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-600 disabled:opacity-50 h-[42px]"
                                onClick={() => handleSearch()}
                                disabled={isCartonListLoading || (searchType === "product" ? !productCode.trim() : !cartonNo.trim())}
                            >
                                {isCartonListLoading ? "Searching..." : "Search"}
                            </button>
                        </div>
                    </div>

                    {/* Carton List Section */}
                    {cartonList.length > 0 && (
                        <div className="bg-white shadow rounded-lg p-4 mb-4">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">Select Cartons</h2>
                                <button
                                    className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600 disabled:opacity-50"
                                    onClick={handleStartEdit}
                                    disabled={selectedCartons.length === 0}
                                >
                                    Edit Assortment
                                </button>
                            </div>

                            <DataTableComponent
                                columns={columns}
                                data={cartonList}
                                loading={isCartonListLoading}
                                totalRows={cartonList.length}
                                keyField="carton_no"
                                pagination
                            />
                        </div>
                    )}

                    {/* Editor Section */}
                    {isEditing && assortmentData && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h2 className="text-lg font-semibold mb-4">Assortment Editor</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {Object.keys(assortmentData).map((size) => (
                                    <div key={size} className="flex flex-col">
                                        <label className="text-sm font-medium text-gray-700 mb-1">
                                            Size {size}
                                        </label>
                                        <input
                                            type="number"
                                            className="border border-gray-300 rounded px-2 py-1 focus:ring-primary focus:border-primary"
                                            value={assortmentData[size]}
                                            onChange={(e) => handleAssortmentChange(size, e.target.value)}
                                            min="0"
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                    onClick={() => setIsEditing(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                                    onClick={handleSubmit}
                                    disabled={loading || isAssortmentLoading}
                                >
                                    {loading || isAssortmentLoading ? "Submitting..." : "Submit Changes"}
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Edited Cartons List Section */}
                    <div className="bg-white shadow rounded-lg p-4 mt-8">
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold">Recently Edited Cartons</h2>
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 disabled:opacity-50"
                                onClick={handleExportEditedCSV}
                                disabled={!editedCartonsData?.body?.editedCartons?.length}
                            >
                                Export CSV
                            </button>
                        </div>
                        {editedCartonsData?.body?.editedCartons?.length > 0 ? (
                            <DataTableComponent
                                columns={editedColumns}
                                data={editedCartonsData.body.editedCartons}
                                keyField="carton_no"
                                pagination
                            />
                        ) : (
                            <div className="text-gray-500 italic p-4 text-center">No cartons have been edited yet.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAssortment;